import React from 'react';
import { AgentAssistCardData, AgentAssistStep, TurnAnalysisEntityRecord } from '../types/api';
import '../styles/components/LiveAnalysisTab.css';

interface AgentAssistCardProps {
  card: AgentAssistCardData;
}

type TodoStatus = 'done' | 'ongoing' | 'next' | 'missed';

const formatEntityLabel = (label?: string, fallback?: string) => {
  if (label && label.trim().length > 0) {
    return label;
  }
  if (!fallback) {
    return '';
  }
  return fallback
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatSteps = (steps: AgentAssistStep[], status: TodoStatus) =>
  steps.map((step) => ({ ...step, status }));

const formatEntityValue = (value?: string | null) => {
  if (value === undefined || value === null) {
    return '\u00A0';
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : '\u00A0';
};

const MAX_ENTITY_ROWS = 5;
const MAX_FILLED_DEFAULT = 2;
const MAX_PENDING_DEFAULT = 3;

const normalizeEntityRecord = (entity: TurnAnalysisEntityRecord) => {
  const value = entity.value || '';
  const filled =
    entity.filled ??
    (typeof value === 'string' ? value.trim().length > 0 : Boolean(value));
  const label = formatEntityLabel(entity.label, entity.entity);
  return {
    ...entity,
    label,
    filled,
  };
};

const buildEntityList = (
  recentEntities: TurnAnalysisEntityRecord[] = [],
  collectedEntities: TurnAnalysisEntityRecord[] = [],
): TurnAnalysisEntityRecord[] => {
  const result: TurnAnalysisEntityRecord[] = [];
  const seen = new Set<string>();

  const filledRecent: TurnAnalysisEntityRecord[] = [];
  const pendingRecent: TurnAnalysisEntityRecord[] = [];
  const filledCollected: TurnAnalysisEntityRecord[] = [];
  const pendingCollected: TurnAnalysisEntityRecord[] = [];

  const categorize = (entities: TurnAnalysisEntityRecord[], targetArrays: { filled: TurnAnalysisEntityRecord[]; pending: TurnAnalysisEntityRecord[] }) => {
    for (const entity of entities) {
      const normalized = normalizeEntityRecord(entity);
      const name = normalized.entity;
      if (!name || seen.has(name)) {
        continue;
      }
      if (normalized.filled) {
        targetArrays.filled.push(normalized);
      } else {
        targetArrays.pending.push(normalized);
      }
      seen.add(name);
    }
  };

  categorize(recentEntities, { filled: filledRecent, pending: pendingRecent });
  categorize(collectedEntities, { filled: filledCollected, pending: pendingCollected });

  const availableFilled = [...filledRecent, ...filledCollected];
  const availablePending = [...pendingRecent, ...pendingCollected];

  const maxFilled = Math.min(MAX_FILLED_DEFAULT, availableFilled.length);
  const maxPending = Math.min(
    MAX_PENDING_DEFAULT,
    Math.min(MAX_ENTITY_ROWS - maxFilled, availablePending.length),
  );

  const pushFrom = (queues: TurnAnalysisEntityRecord[][], limit: number, counter: { count: number }) => {
    for (const queue of queues) {
      while (queue.length && counter.count < limit && result.length < MAX_ENTITY_ROWS) {
        const entity = queue.shift()!;
        result.push(entity);
        counter.count += 1;
      }
    }
  };

  const filledCounter = { count: 0 };
  pushFrom([filledRecent, filledCollected], maxFilled, filledCounter);

  const pendingCounter = { count: 0 };
  pushFrom([pendingRecent, pendingCollected], maxPending, pendingCounter);

  if (result.length < MAX_ENTITY_ROWS) {
    const leftovers = [
      ...filledRecent,
      ...filledCollected,
      ...pendingRecent,
      ...pendingCollected,
    ];
    for (const entity of leftovers) {
      if (result.length >= MAX_ENTITY_ROWS) {
        break;
      }
      if (result.some((existing) => existing.entity === entity.entity)) {
        continue;
      }
      result.push(entity);
    }
  }

  return result.slice(0, MAX_ENTITY_ROWS);
};

const AgentAssistCard: React.FC<AgentAssistCardProps> = ({ card }) => {
  const completed = formatSteps(card.completed_steps.slice(-2), 'done');
  const ongoing = (card.ongoing_steps ?? []).map((s) => ({
    step: s.step ?? '',
    state: undefined as string | undefined,
    status: 'ongoing' as TodoStatus,
  }));
  const upcoming = formatSteps(card.upcoming_steps, 'next');
  const missed = formatSteps(card.missed_steps, 'missed');

  const todoList = [...completed, ...ongoing, ...upcoming, ...missed];
  const arrowIndex =
    todoList.findIndex((step) => step.status === 'ongoing') >= 0
      ? todoList.findIndex((step) => step.status === 'ongoing')
      : todoList.findIndex((step) => step.status === 'next');
  const nextActionIndex = arrowIndex >= 0 ? arrowIndex : -1;
  const entityList = buildEntityList(
    card.recent_collected_entities || [],
    card.collected_entities || [],
  );

  return (
    <div className="agent-assist-card">
      <div className="agent-card-body">
        <div className="agent-card-todo">
          <span className="agent-card-section-title">To‑Dos</span>
          {card.signals && card.signals.some(
              (signal) =>
                signal.value &&
                typeof signal.value === 'string' &&
                signal.value.trim().length > 0 &&
                signal.value.toLowerCase() !== 'none' &&
                signal.type !== 'intent'
            ) && (
            <div className="agent-card-signal-row">
              {card.signals
                .filter(
                  (signal) =>
                    signal.value &&
                    typeof signal.value === 'string' &&
                    signal.value.trim().length > 0 &&
                    signal.value.toLowerCase() !== 'none' &&
                    signal.type !== 'intent'
                )
                .map((signal, index) => {
                  const icon =
                    signal.type === 'sentiment'
                      ? 'mood'
                      : signal.type === 'emotion'
                      ? 'map_pin_heart'
                      : 'info';
                  return (
                    <span
                      key={`${signal.type}-${index}`}
                      className={`agent-card-signal-pill signal-${signal.type}`}
                    >
                      <span className="material-symbols-rounded signal-icon">
                        {icon}
                      </span>
                      {/* {signal.type}: {signal.value} */}
                      {signal.value}
                    </span>
                  );
                })}
            </div>
          )}
          {card.free_flow_state_title && (
            <div
              className={`agent-card-state-line ${
                card.strict_state_title &&
                card.strict_state_title !== card.free_flow_state_title
                  ? 'warning'
                  : ''
              }`}
            >
              {card.strict_state_title &&
              card.strict_state_title !== card.free_flow_state_title ? (
                <>
                  At {card.free_flow_state_title},{' '}
                  <strong>must proceed to {card.strict_state_title}.</strong>
                </>
              ) : (
                <>At {card.free_flow_state_title}</>
              )}
            </div>
          )}

          {(card.violations ?? []).length > 0 && (
            <div className="agent-card-violations">
              {(card.violations ?? []).map((text, index) => (
                <div key={`violation-${index}`} className="agent-card-violation-item">
                  {text}
                </div>
              ))}
            </div>
          )}
          {todoList.length === 0 && (card.violations ?? []).length === 0 && (
            <span className="agent-card-empty">No guidance available</span>
          )}
          <div className="agent-card-todo-list">
            {todoList.map((step, index) => (
              <div
                key={`todo-${index}`}
                className={`agent-card-todo-item status-${step.status}`}
                title={step.state || undefined}
              >
                <span className="agent-card-status-wrapper">
                  <span className="agent-card-status-indicator">
                    {step.status === 'done' ? '✓' : step.status === 'ongoing' ? '●' : ''}
                  </span>
                </span>
                <div className="agent-card-step-copy">
                  <span className="agent-card-step-name">
                    {step.step || 'Unnamed step'}
                  </span>
                  {index === nextActionIndex && (
                    <span className="agent-card-next-arrow">
                      ←
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
 
          <div className="todo-protip-wrapper">
          {card.pro_tip && (
            <div className="agent-card-protip-inline">
              <span className="protip-icon">i</span>
              {card.pro_tip}
            </div>
          )}
          </div>
        </div>

        <div className="agent-card-info">
          <span className="agent-card-section-title">Info</span>
          {entityList.length === 0 && (
            <span className="agent-card-empty">No details captured yet</span>
          )}
          {card.signals &&
            card.signals.some(
              (signal) =>
                signal.type === 'intent' &&
                signal.value &&
                typeof signal.value === 'string' &&
                signal.value.trim().length > 0 &&
                signal.value.toLowerCase() !== 'none'
            ) && (
              <div className="agent-card-intent-pill">
                Customer Intent:{' '}
                {
                  card.signals.find(
                    (signal) => signal.type === 'intent' && signal.value
                  )?.value
                }
              </div>
            )}
          {entityList.length > 0 && (
            <div className="agent-card-info-block">
              <div className="agent-card-entity-form">
                {entityList.map((entity, index) => (
                  <div
                    key={`state-${entity.entity}-${index}`}
                    className={`entity-form-row ${entity.filled ? 'filled' : 'pending'}`}
                    title={`${formatEntityLabel(entity.label, entity.entity)}: ${entity.value || '—'}`}
                  >
                    <span className="entity-form-label">
                      {formatEntityLabel(entity.label, entity.entity)}
                    </span>
                    <span className="entity-form-value">{formatEntityValue(entity.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {card.attention.length > 0 && (
        <div className="agent-card-alerts">
          {card.attention.map((warning, index) => (
            <div key={`warn-${index}`} className="agent-card-warning">
              {warning}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentAssistCard;

